/** Which epic lanes on the board are open. Derived, not synced: lanes are
 *  folded by default, open by default while a filter narrows the board (so
 *  its matches show), and open by default when there is only one lane.
 *  Each default keeps the user's own exceptions to it: `opened` for the
 *  folded default, `folded` for the open one — the latter belongs to one
 *  filter and is forgotten when the filter changes. */
export type LaneFolds = {
  opened: ReadonlySet<string>
  folded: { filterKey: string; ids: ReadonlySet<string> }
}

export const NO_FOLDS: LaneFolds = { opened: new Set(), folded: { filterKey: "", ids: new Set() } }

const openByDefault = (filterKey: string, laneCount: number) => Boolean(filterKey) || laneCount === 1

const foldedFor = (folds: LaneFolds, filterKey: string): ReadonlySet<string> =>
  folds.folded.filterKey === filterKey ? folds.folded.ids : new Set()

export function isLaneOpen(folds: LaneFolds, id: string, filterKey: string, laneCount: number) {
  return openByDefault(filterKey, laneCount) ? !foldedFor(folds, filterKey).has(id) : folds.opened.has(id)
}

export function toggleLane(folds: LaneFolds, id: string, filterKey: string, laneCount: number): LaneFolds {
  const flip = (ids: ReadonlySet<string>) => {
    const next = new Set(ids)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    return next
  }
  return openByDefault(filterKey, laneCount)
    ? { ...folds, folded: { filterKey, ids: flip(foldedFor(folds, filterKey)) } }
    : { ...folds, opened: flip(folds.opened) }
}
